/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ROUTES } from 'constants';

import isNil from 'lodash/isNil';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import qs from 'qs';
import noop from 'lodash/noop';
import { defineMessages } from 'react-intl';

import { CAPACITY_UNITS_PRECISION } from '../Services/constants/Constants';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { RESOURCE_TYPES } from 'dataConstants';

const messages = defineMessages({
  noAccess:
    "You don't have access to this data. To get access, contact your application administrator",
});

export const sanitize = str => str.replace(/[`~!@#$%^&*()|+=?;:'",.<>{}[\]\\\/]/g, ''); // eslint-disable-line no-useless-escape

/**
 * https://github.com/reactjs/reactjs.org/blob/ac0f71dca61afaf9a0371e1171a5e07657af73ff/content/blog/2015-12-16-ismounted-antipattern.md
 *
 * @param {*} promise
 */
export const makeCancelable = promise => {
  let cancel = noop;

  const wrappedPromise = new Promise((resolve, reject) => {
    cancel = () => {
      resolve = null; // eslint-disable-line no-param-reassign
      reject = null; // eslint-disable-line no-param-reassign
    };

    promise.then(
      val => {
        if (resolve) {
          resolve(val);
        }
      },
      error => {
        if (reject) {
          reject(error);
        }
      }
    );
  });

  wrappedPromise.cancel = cancel;

  return wrappedPromise;
};

/** Checks both the query string and hostname for a tenant */
export const determineTenantIdFromURL = (search, hostname) => {
  let tenantid = null;

  if (search && search.length > 1) {
    const { tenantid: newTenantId, tenantId: newTenantId2 } = qs.parse(search.slice(1));
    if (newTenantId || newTenantId2) {
      tenantid = newTenantId || newTenantId2;
    }
  }

  if (!tenantid && hostname.includes('.monitor.')) {
    tenantid = hostname.split('.monitor.')?.[0];
  }

  if (tenantid) {
    return sanitize(tenantid);
  }
  return tenantid;
};

export const formatUsageNumber = (formatNumber, origNum, unit) =>
  formatNumber(origNum, {
    // default of 2, but check the units
    minimumFractionDigits:
      unit && !isNil(CAPACITY_UNITS_PRECISION[unit]) ? CAPACITY_UNITS_PRECISION[unit] : 2,
    maximumFractionDigits:
      unit && !isNil(CAPACITY_UNITS_PRECISION[unit]) ? CAPACITY_UNITS_PRECISION[unit] : 2,
  });

export const isEmailFormatValid = emailStr => {
  let isValid = false;
  if (emailStr.length > 0) {
    const re = new RegExp(
      '^((\\s*[a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5}){1,25})\\s*([;,]((\\s*[a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5}){1,25})+\\s*)*$'
    );
    isValid = re.test(emailStr);
  }
  return isValid;
};

export const isValidIPRegExp = value =>
  /\b(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/i.test(
    value
  );

export const isValidURLRegExp = value =>
  /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value
  );

/**
 * validate if the url is valid or not with optional protocol validation
 * @param {*} url complete URL path with protocols
 * @param {*} protocol type of the protocol. ex: https: , http:
 * @returns
 */
export const isValidURL = (url, protocol) => {
  if (url) {
    try {
      const urlString = new URL(url);

      const validURLRegEx = isValidIPRegExp(url) || isValidURLRegExp(url);

      return protocol ? validURLRegEx && protocol === urlString.protocol : validURLRegEx;
    } catch (e) {
      return false;
    }
  }
  return false;
};

export const isValidHttpsURL = urlString => isValidURL(urlString, 'https:');

export const isValidHttpURL = urlString => isValidURL(urlString, 'http:');

export const handleKeyDown = (evt, callback) => {
  if (evt.which === 13) {
    callback(evt);
  }
};

/**
 * Validates the filter object for api request bodies
 * @param {*} val Each value in the filter object
 * @returns {boolean}
 */
export const isApiFilterValid = val =>
  typeof val === 'object' || isNil(val) ? false : typeof val === 'string' ? val.length > 0 : true;

/**
 *
 * @param {*} arr array to search
 * @param {*} predicate lodash object predicate to use for search
 * @param {*} newval new object value to insert
 * Creates a new array each time so the simple array comparison will trigger for downstream hooks
 */
export const upsert = (arr, predicate, newval) => {
  const match = find(arr, predicate);
  const newArray = [...arr];
  if (match) {
    const index = findIndex(arr, predicate);
    newArray.splice(index, 1, { ...match, ...newval });
  } else {
    newArray.push(newval);
  }
  return newArray;
};

/**
 * Extension of Error class that can store the i18n message object and format it
 * @type Error
 */
export class I18NError extends Error {
  /**
   * Creates a new I18NError
   * @param Object i18nMessage object generated by react-intl has an id and defaultMessage
   * @param Object values object to populate message if needed
   */
  constructor(i18nMessage, values = {}) {
    super(i18nMessage.defaultMessage);
    this.i18nMessage = i18nMessage;
    this.values = values;
    this.response = values?.response ? { message: formatErrorResponse(values) } : values;
  }
}

/**
 * Checks all the common errors thrown by the API backend and resolves them to an object
 * @param {*} error
 * @param {*} formatMessage
 * @returns object with key message with the renderable error message
 */
export const formatError = (error, formatMessage) => ({
  message: error?.i18nMessage
    ? formatMessage(error.i18nMessage, error.values)
    : error?.message ?? error,
});

/** returns title and subtitle */
export const formatAppError = (title, error, formatMessage) => ({
  title: formatMessage(title),
  subtitle: error.i18nMessage
    ? formatMessage(error.i18nMessage, error.values)
    : error.message || error,
});

/**
 * will determine whether we've logged this error in this session.
 * @param  {[type]}  error [description]
 * @return {Boolean}       [description]
 * @affects Sets the sessionStorage to indicate we've now seen this error and don't log it again.
 */
export const hasErrorBeenLogged = error => {
  const errorKey = Object.values(error).join('-');

  // Only update the state if we haven't seen this error before
  if (!sessionStorage.getItem(errorKey)) {
    sessionStorage.setItem(errorKey, 'seen');
    return false;
  }
  return true;
};

/**
 * Handles promises given by Promise.allSettled
 * @param promise promise to check
 * @param resultDefault fallback result if promise errors
 * @returns
 */
export const getSettledPromiseResult = (promise, resultDefault) =>
  promise.status === 'fulfilled' ? promise.value : resultDefault;

/**
 * This is a utility method that will execute a callbackover an array or single value and return a single promise that will return the array of their results
 * id - is either array or single value
 * callback - is the function to call with each array
 */
export const mapCallbackOverArrayOrSingleValue = (id, callback) => {
  const promiseArray = [];
  if (Array.isArray(id)) {
    id.forEach(eachId => promiseArray.push(callback(eachId)));
  } else {
    promiseArray.push(callback(id));
  }
  return Promise.all(promiseArray);
};

/** returns the monitor api base url */
export const getAPIBaseURL = (isICP, hostname) => {
  const apiBaseURL = 'https://api-dev.connectedproducts.internetofthings.ibmcloud.com';

  if (hostname) {
    if (isICP) {
      return `https://${hostname.replace('.monitor.', '.api.monitor.')}`;
    }
    if (hostname.includes('dashboard-beta')) {
      return 'https://api-beta.connectedproducts.internetofthings.ibmcloud.com';
    }
    if (hostname.includes('dashboard-us')) {
      return 'https://api-us.connectedproducts.internetofthings.ibmcloud.com';
    }
    if (hostname.includes('dashboard-uk')) {
      return 'https://api-uk.connectedproducts.internetofthings.ibmcloud.com';
    }
    if (hostname.includes('dashboard-eu')) {
      return 'https://api-eu.connectedproducts.internetofthings.ibmcloud.com';
    }
    if (hostname.includes('dashboard-us-staging')) {
      return 'https://api-us-staging.connectedproducts.internetofthings.ibmcloud.com';
    }
  }

  return apiBaseURL;
};

/**
 * Returns an image object with id and a local url that can be displayed
 *
 * @param {*} id the id of the image to retrieve
 * @param {*} filename the filename of the image to retrieve
 * @param {*} response the response that retrieved the image blob
 * @param {*} entityTypeId the entityTypeId that the image belogs (Used only for Device type instnace and summary dashboards)
 * @param {*} entity the entity that the image belogs (Used only for Instnace Dashboard)
 */
export const calculateLocalImage = (id, filename, response, entityTypeId = null, entity = null) =>
  response
    ?.blob()
    .then(blob => URL.createObjectURL(blob))
    .then(src => ({
      src,
      id,
      alt: filename,
      title: filename,
      ...(!isNil(entityTypeId) ? { entityTypeId } : {}),
      ...(!isNil(entity) ? { entity } : {}),
    }));

/**
 * Returns a list of resource UUIDs that the user has access to
 * @param {array} allResourceUUIDs
 * @param {array} allowedResourcesUUIDs
 * @returns {array}
 */
export const filterResources = (allResourceUUIDs, allowedResourcesUUIDs) => {
  let allowedIdsArray = [];

  // get a single array of ids from the allowed resources object
  Object.keys(allowedResourcesUUIDs)
    .filter(key => key !== RESOURCE_TYPES.DEVICE) // TODO - remove if user resource API returns only an array of uids
    .forEach(allowedKey => {
      allowedIdsArray = [...allowedIdsArray, ...allowedResourcesUUIDs[allowedKey]];
    });

  return allResourceUUIDs.filter(resourceUUID => allowedIdsArray.includes(resourceUUID));
};
/**
 *  Filters alerts based in user access and returns an array
 * @param {*} alerts
 * @param {*} currentUserResources
 * @returns {array}
 */
export const filterUserAlertResources = (alerts, currentUserResources) => {
  if (!isEmpty(currentUserResources) && alerts && !isEmpty(alerts)) {
    return alerts.filter(alert => {
      const {
        deviceDto,
        resourceDto: { uuid: resourceUUID, type: resourceType },
      } = alert ?? {};

      if (deviceDto) {
        // if device level alert then use deviceDto
        const deviceTypeUUId = deviceDto.deviceTypeUUId ?? resourceUUID;

        return (
          deviceDto.name &&
          currentUserResources[RESOURCE_TYPES.DEVICE]?.[deviceTypeUUId]?.includes(deviceDto.name)
        );
      }

      if (resourceType && resourceUUID) {
        // if any resource other than device use resourceDto
        return currentUserResources[resourceType]?.includes(resourceUUID);
      }

      return false;
    });
  }
  return alerts ?? [];
};

/**
 * Filters user resources and returns an array
 * @param {array} entityTypes
 * @param {object} dataCache
 * @param {string} type
 * @param {string} parentUUId
 * @returns {array}
 */
export const filterUserResourcesToArray = (entityTypes, dataCache, type = '', parentUUId = '') => {
  if (entityTypes && !isEmpty(entityTypes)) {
    const resourceUUIDs = dataCache.get('currentUserResources'); // get current resources from datacache/redux

    // if no UUIDs => user has access to all resources => return
    if (isEmpty(resourceUUIDs)) {
      return entityTypes;
    }

    // process only resources of specified type
    let typeResourcesUUIDs = resourceUUIDs;
    if (type && resourceUUIDs[type]) {
      typeResourcesUUIDs = { [type]: resourceUUIDs[type] };
    }

    // workaround for devices
    let allowedDevices = [];
    if (!type || type === RESOURCE_TYPES.DEVICE) {
      allowedDevices = entityTypes.filter(device => {
        const deviceTypeUUId = device?.deviceTypeUUId || parentUUId;
        return (
          device?.name &&
          typeResourcesUUIDs[RESOURCE_TYPES.DEVICE]?.[deviceTypeUUId]?.includes(device.name)
        );
      });
    }

    const entityTypesUUIDs = entityTypes.map(({ uuid }) => uuid); // get uuid of resources
    const allowedUUIDs = filterResources(entityTypesUUIDs, typeResourcesUUIDs); // filter allowed UUIDs

    return [
      ...entityTypes.filter(entityType => allowedUUIDs.includes(entityType.uuid)),
      ...allowedDevices,
    ];
  }

  return entityTypes;
};

/**
 * Filters user resources and returns an object
 * @param {object} responseJson
 * @param {object} dataCache
 * @param {string} type
 * @param {string} parentUUId
 * @returns {object}
 */
export const filterUserResourcesToObject = (
  responseJson,
  dataCache,
  type = '',
  parentUUId = ''
) => {
  if (responseJson) {
    const { results: initialEntityTypes, totalCount } = responseJson;
    const filteredResults = filterUserResourcesToArray(
      initialEntityTypes,
      dataCache,
      type,
      parentUUId
    );

    return {
      results: filteredResults || [],
      totalCount: totalCount || 0,
    };
  }
  return responseJson;
};

/**
 * Checks if user has access to specific resource
 * @param {string} resourceUUID
 * @param {object} allUserResources
 * @param {object} formatMessage
 * @param {string} type
 * @param {string} parentUUId
 * @returns {boolean}
 */
export const checkUserAccessToResource = (
  resourceUUID,
  allUserResources,
  formatMessage = () => {},
  type = '',
  parentUUId = ''
) => {
  const foundResources = filterUserResourcesToArray(
    [{ [type === RESOURCE_TYPES.DEVICE ? 'name' : 'uuid']: resourceUUID }],
    { get: () => allUserResources },
    type,
    parentUUId
  );

  if (!foundResources?.length) {
    window.location.replace(
      `${ROUTES.NOMATCH}?${qs.stringify({
        invalidURL: window.location.href,
        error: formatMessage(messages.noAccess),
        icon: 'not-authorized',
      })}`
    );
    return false;
  }
  return true;
};
